// components/doctors/DoctorsList.jsx
import React from 'react';
import { motion } from 'framer-motion';

const COLORS = {
  primary: "#0760ad",
  secondary: "#118a94",
  white: "#FFFFFF"
};

const DoctorsList = ({ doctors, loading, setSelectedDoctor, onBookNow }) => {
  // Helper function to format availability
  const formatAvailability = (doctor) => {
    if (!doctor.availability || !doctor.working_hours) return null;
  
    const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const availableDays = Array.isArray(doctor.availability) ? doctor.availability : [];
  
    const sortedDays = availableDays.sort((a, b) =>
      dayOrder.indexOf(a) - dayOrder.indexOf(b)
    );
  
    const groupedDays = [];
    let currentGroup = null;
  
    sortedDays.forEach((day) => {
      const hours = doctor.working_hours?.[day];
      if (!hours) return;
  
      const timeStr = `${hours.start} – ${hours.end}`; // use en dash for cleaner look
  
      if (
        currentGroup &&
        currentGroup.time === timeStr &&
        dayOrder.indexOf(day) === dayOrder.indexOf(currentGroup.days.at(-1)) + 1
      ) {
        currentGroup.days.push(day);
      } else {
        if (currentGroup) groupedDays.push(currentGroup);
        currentGroup = { days: [day], time: timeStr };
      }
    });
  
    if (currentGroup) groupedDays.push(currentGroup);
  
    return groupedDays.map((group, index) => {
      const abbreviate = (d) => d.slice(0, 3);
  
      let dayLabel;
      if (group.days.length === 1) {
        dayLabel = abbreviate(group.days[0]);
      } else {
        const first = group.days[0];
        const last = group.days[group.days.length - 1];
        const isConsecutive =
          dayOrder.indexOf(last) - dayOrder.indexOf(first) + 1 === group.days.length;
  
        dayLabel = isConsecutive
          ? `${abbreviate(first)}–${abbreviate(last)}`
          : group.days.map(abbreviate).join(', ');
      }
  
      return (
        <div key={index} className="flex justify-between text-xs text-gray-700">
          <span>{dayLabel}</span>
          <span className="ml-2 text-gray-600">{group.time}</span>
        </div>
      );
    });
  };
  
  

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold" style={{ color: COLORS.primary }}>
          Available Doctors ({doctors.length})
        </h2>
      </div>

      {loading ? (
        <div className="bg-white p-8 rounded-lg shadow-sm text-center">
          <p className="text-gray-600">Loading doctors...</p>
        </div>
      ) : doctors.length === 0 ? (
        <div className="bg-white p-8 rounded-lg shadow-sm text-center">
          <p className="text-gray-600">No doctors match your current filters.</p>
          <p className="text-gray-600 mt-2">Try adjusting your search criteria.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {doctors.map((doctor, index) => (
            <motion.div
              key={doctor.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-all"
            >
              <div className="p-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0 mr-4">
                    <div className="h-20 w-20 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                      {doctor.image_url ? (
                        <img src={doctor.image_url} alt={doctor.name} className="h-full w-full object-center" />
                      ) : (
                        <span className="text-2xl text-gray-500">
                          {doctor.gender === 'Female' ? '👩‍⚕️' : '👨‍⚕️'}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900">{doctor.name}</h3>
                    <p className="text-[#118a94] font-medium">
                      {doctor.specialty.map(s => s.name).join(', ')}
                    </p>
                    <p className="text-gray-600 text-sm mt-1">{doctor.qualification}</p>
                    
                    {/* Availability Section */}
                    <div className="mt-2">
                      <div className="flex items-center text-sm text-gray-600 mb-1">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="font-medium">Availability</span>
                      </div>
                      <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2 space-y-1 text-xs text-gray-700">
                            {formatAvailability(doctor) || (
                              <p className="text-gray-400">Not specified</p>
                            )}
                          </div>
                    </div>
                    
                    <div className="mt-3 flex space-x-2">
                      <button
                        onClick={() => setSelectedDoctor(doctor)}
                        className="px-4 py-2 text-sm font-medium text-[#0760ad] border border-[#0760ad] rounded-md hover:bg-[#0760ad] hover:text-white transition"
                      >
                        View Profile
                      </button>
                      <button
                        onClick={() => onBookNow(doctor)}
                        className="px-4 py-2 text-sm font-medium text-white bg-[#0760ad] rounded-md hover:bg-[#098a48] transition"
                      >
                        Book Now
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DoctorsList;